import { useEffect, useState } from "react";
import {
  Calendar01Icon,
  Globe02Icon,
  MapsIcon,
  CheckmarkSquare02Icon,
} from "hugeicons-react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import { currencyFormat } from "../../utils/currency.util";
import { useLocation, Link, useParams } from "react-router-dom";
import moment from "moment";
import { loadStripe } from "@stripe/stripe-js";
import { STRIPE_PUBLIC_KEY } from "../../utils/constans";
import { api } from "../../Services/api-service";
import { eventEmitter } from "../../utils/helpers/eventEmitter";

const TicketPage = () => {
  const location = useLocation();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);

  const [event, setEvent] = useState(location.state?.event);
  const [tickets, setTickets] = useState([]);

  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromoCode, setAppliedPromoCode] = useState(null);

  // Cargar el evento y tickets si no viene en location.state
  useEffect(() => {
    const fetchEventData = async () => {
      if (!event) {
        setIsLoading(true);
        try {
          let eventId = id;

          // Si no hay ID desde useParams, extraerlo de la URL
          if (!eventId) {
            const match = location.pathname.match(/\/events\/([^/]+)\/tickets/);
            eventId = match ? match[1] : null;
          }

          if (eventId) {
            const response = await api.get(`/events/details/${eventId}`);
            if (response.data) {
              setEvent(response.data);
              setTickets(response.data.tickets || []);
            }
          }
        } catch (error) {
          console.error("Error loading event:", error);
        } finally {
          setIsLoading(false);
        }
      } else {
        // Si el event ya existe en state, solo extraemos los tickets
        setTickets(event.tickets || []);
      }
    };

    fetchEventData();
  }, [event, id, location.pathname]);

  const handleBuyClick = async (e, ticket) => {
    e.preventDefault();
    console.log("ticket", ticket);
    try {
      const stripePromise = await loadStripe(STRIPE_PUBLIC_KEY);

      const requestBody = {
        eventId: event?.id,
        ticketId: ticket?.id,
      };

      if (appliedPromoCode) {
        requestBody.promoCode = appliedPromoCode;
      }

      const response = await api.post(`/events/guests/add`, requestBody);

      if (response.data.status === "pending") {
        eventEmitter.emit("SHOW_TICKET_MODAL", {
          message: response.data.message,
          onClose: () => {
            window.location.href = `/events/${event.id}`;
          },
        });
        return;
      } else if (response.data.status === "approved") {
        window.location.href = `/events/${event.id}`;
        return;
      }

      if (!response?.data?.id) return;

      const session = await response.data?.id;
      stripePromise.redirectToCheckout({
        sessionId: session,
      });
    } catch (error) {
      console.error("Payment error:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Loading event information...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            Error loading ticket: Event not found
          </p>
          <Link to="/my-tickets">
            <button className="bg-spark-primary text-white px-4 py-2 rounded-md">
              Back to My Tickets
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold">{event.title}</h1>
          <div className="mt-2 flex items-center justify-center gap-2 text-muted-foreground">
            {event.type === "online" ? (
              <>
                <Globe02Icon className="h-5 w-5" />

                <span>Online Event</span>
              </>
            ) : (
              <>
                <MapsIcon className="h-5 w-5" />

                <span>In-person Event</span>
              </>
            )}
          </div>
        </div>

        {/* Replace the existing promo code section with this */}
        <div className="mt-4 mb-4 flex items-end justify-end gap-2">
          {!showPromoInput && !appliedPromoCode ? (
            <button
              className="text-sm text-spark-600 hover:text-spark-800 font-medium"
              onClick={() => setShowPromoInput(true)}
            >
              Have a promo code?
            </button>
          ) : appliedPromoCode ? (
            <div className="text-sm">
              <span className="text-green-600 font-medium">
                PROMOCODE: {appliedPromoCode} will be used
              </span>
              <button
                className="ml-2 text-red-500 hover:text-red-700"
                onClick={() => {
                  setAppliedPromoCode(null);
                  setPromoCode("");
                }}
              >
                ✕
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Enter code"
                className="text-sm border rounded px-2 py-1 w-100"
              />
              <button
                className="text-sm text-white bg-spark-600 hover:bg-spark-700 px-2 py-1 rounded"
                onClick={() => {
                  if (promoCode.trim()) {
                    setAppliedPromoCode(promoCode.trim());
                    setShowPromoInput(false);
                  }
                }}
              >
                Apply
              </button>
              <button
                className="text-sm text-gray-500 hover:text-gray-700 ml-1"
                onClick={() => {
                  setShowPromoInput(false);
                  setPromoCode("");
                }}
              >
                ✕
              </button>
            </div>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {tickets.map((ticket, index) => (
            <Card key={index} className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-spark-500/10 to-transparent pointer-events-none" />

              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-2xl font-bold">
                      {ticket.name}
                    </CardTitle>
                    {ticket.name !== "Get Your Matches" && (
                      <>
                        <p className="mt-2 text-3xl font-bold text-spark-600">
                          {currencyFormat(ticket.currency, ticket.price)}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="space-y-2">
                  {ticket.name !== "Get Your Matches" && (
                    <>
                      <div className="flex items-start gap-2">
                        <Calendar01Icon className="h-5 w-5 text-spark-600 shrink-0" />
                        <div>
                          <p className="font-medium">
                            {moment(event.startAt).format(
                              "dddd, MMMM Do YYYY hh:mm A"
                            )}
                          </p>
                        </div>
                      </div>
                      {event.type !== "online" && (
                        <>
                          <div className="flex items-start gap-2">
                            <MapsIcon className="h-5 w-5 text-spark-600 shrink-0" />
                            <p>{event.address}</p>
                          </div>
                        </>
                      )}
                    </>
                  )}
                  {ticket.description != null && (
                    <p className="font-medium">
                      {ticket.description || "No description"}
                    </p>
                  )}
                </div>

                {ticket.name !== "Get Your Matches" && (
                  <>
                    <Separator />

                    <div>
                      <div className="mb-3 flex items-center gap-2">
                        <h3 className="font-semibold">What's included:</h3>
                      </div>
                      <ul className="space-y-2">
                        {ticket.included.map((item, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <CheckmarkSquare02Icon className="h-5 w-5 text-spark-600" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </CardContent>

              <CardFooter>
                <button
                  className="mt-4 bg-spark-primary text-white rounded-lg p-2 w-full hover:bg-spark-800 transition-colors"
                  onClick={(e) => {
                    console.log("ticket", ticket);
                    handleBuyClick(e, ticket);
                  }}
                >
                  {ticket.name !== "Get Your Matches" ? (
                    "Get your matchets"
                  ) : (
                    <>
                      Purchase For{" "}
                      {currencyFormat(ticket.currency, ticket.price)}
                    </>
                  )}
                </button>
              </CardFooter>
            </Card>
          ))}
        </div>

        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>
            Hosted By:{" "}
            {event.hosts.map((host, i) => (
              <span key={host.id}>
                {host.name}
                {i > event.hosts.lenght && ", "}
              </span>
            ))}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TicketPage;
